import { Pencil, Plus, Trash2, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { TeeSwatch } from "./common/TeeBadge";
import type { Clubhouse_LeagueV2RosterMemberDto } from "../types/leagueRoster";

/**
 * The league's substitute pool, beside the roster rather than inside it.
 *
 * Substitutes used to be rows in the main table, told apart by a Substitute
 * Yes/No column — which spent a column on a distinction the operator reads as
 * "these people are not playing unless someone drops out". Standing them up
 * as their own list says the same thing with no column at all, and leaves the
 * table to describe the people who ARE playing.
 *
 * Name and text only, like the table: no avatars anywhere in this workflow.
 * The tee uses the table's dot-and-label, so the two lists read as one system
 * rather than two components that happen to sit side by side.
 */
export function LeagueSubstitutesRail({
  substitutes,
  canEdit,
  onEdit,
  onRemove,
  onAdd,
}: {
  substitutes: Clubhouse_LeagueV2RosterMemberDto[];
  canEdit: boolean;
  onEdit: (member: Clubhouse_LeagueV2RosterMemberDto) => void;
  /**
   * Removal has to live here as well as in the table. A substitute is no
   * longer a row in the players table, so the table's Remove no longer
   * reaches them — without this, adding a substitute would be a one-way door.
   */
  onRemove: (member: Clubhouse_LeagueV2RosterMemberDto) => void;
  onAdd: () => void;
}) {
  return (
    <aside className="flex h-fit flex-col gap-4 rounded-2xl border border-border bg-card px-5 py-4.5">
      <h3 className="poppins -mx-5 border-b border-border px-5 pb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.06em] text-text-muted">
        <Users className="size-4" />
        Substitutes
      </h3>

      {substitutes.length === 0 ? (
        <p className="mulish text-xs text-text-muted">
          No substitutes yet. Add one to cover a player who cannot make a round.
        </p>
      ) : (
        <ul className="m-0 flex list-none flex-col gap-0 p-0">
          {substitutes.map((member, i) => (
            <li
              key={String(member.memberId)}
              // A rule under every row, the last one included: it doubles as
              // the line that separates the list from Add Substitute.
              className={cn(
                "-mx-5 border-b border-border px-5 pb-3",
                i > 0 && "pt-3",
              )}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex min-w-0 flex-col gap-1">
                  <span
                    className="poppins truncate text-sm font-extrabold text-text-primary"
                    title={member.playerName ?? undefined}
                  >
                    {member.playerName ?? "—"}
                  </span>
                  {/* Tee and handicap on one line under the name, the same
                      pairing the table puts under a player's name. */}
                  <span className="flex min-w-0 items-center gap-1.5">
                    <TeeSwatch teeType={member.teeType} />
                    <span
                      className="mulish text-xs text-text-muted"
                      aria-hidden
                    >
                      ·
                    </span>
                    <span className="mulish text-xs text-text-muted tabular-nums">
                      {member.currentHandicap != null ||
                      member.seedHandicap != null
                        ? `${(member.currentHandicap ?? member.seedHandicap ?? 0).toFixed(1)} hcp`
                        : "No handicap yet"}
                    </span>
                  </span>
                </div>
                {/* Pencil and trash, the same pair the players table uses, so a
                    substitute can be taken off the roster the same way a
                    player can. */}
                <span className="flex shrink-0 items-center gap-0.5">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="size-8 rounded-md p-0"
                    disabled={!canEdit}
                    title="Edit substitute"
                    onClick={() => onEdit(member)}
                  >
                    <Pencil data-icon />
                    <span className="sr-only">
                      Edit {member.playerName ?? "substitute"}
                    </span>
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="size-8 rounded-md p-0"
                    disabled={!canEdit}
                    title="Remove substitute"
                    onClick={() => onRemove(member)}
                  >
                    <Trash2 data-icon />
                    <span className="sr-only">
                      Remove {member.playerName ?? "substitute"}
                    </span>
                  </Button>
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}

      <Button
        type="button"
        variant="outline"
        size="sm"
        className="mulish h-[38px] w-full gap-1.5 rounded-lg border-dashed text-xs"
        disabled={!canEdit}
        onClick={onAdd}
      >
        <Plus data-icon="inline-start" />
        Add Substitute
      </Button>
    </aside>
  );
}
